import { Tags } from "../ui";
import { appLabel, sectionLabel } from "./reportAnalysisModel";
import { count, periodLabel } from "./format";

// One check result (error or warning) with every detail the backend returned for it:
//   header      MISSING_REQUIRED_COLUMNS / WRONG_PROVIDER: missing, expected and actual columns, suggestions
//   branches    BRANCH_* / CROSS_STORE_BRANCH_MISMATCH: the file's branches (name + ID as the file
//               shows them), matched and unmatched, with what happens to their rows; long lists collapse
//   period      ROWS_OUTSIDE_SELECTED_PERIOD (daily) / SOURCE_PERIOD_DIFFERS_FROM_SELECTED (monthly) /
//               NO_ROWS_IN_PERIOD / SOURCE_PERIOD_INCONSISTENT: months with rows and treatment, row counts
// Nothing here is guessed: a detail the answer does not carry is not shown.

const HEADER = new Set(["MISSING_REQUIRED_COLUMNS", "WRONG_PROVIDER"]);
const CROSS_STORE = "CROSS_STORE_BRANCH_MISMATCH";
const NOT_MATCHED = "BRANCH_NOT_MATCHED";
const BRANCH = new Set([NOT_MATCHED, "BRANCH_BELONGS_TO_ANOTHER_STORE", CROSS_STORE]);
const BRANCH_PREVIEW = 5;
const DAILY_PERIOD = "ROWS_OUTSIDE_SELECTED_PERIOD";
const MONTHLY_PERIOD = "SOURCE_PERIOD_DIFFERS_FROM_SELECTED";
const PERIOD = new Set([DAILY_PERIOD, MONTHLY_PERIOD, "PERIOD_MISMATCH", "NO_ROWS_IN_PERIOD", "SOURCE_PERIOD_INCONSISTENT"]);
const COLUMN_PREVIEW = 12;

// "May 2026 — 100 rows, excluded" (the treatment only when the backend states it).
const periodsText = (list, lang, text) => (list || []).filter((item) => item?.month).map((item) => `${periodLabel(item, lang)} — ${text.details.rows(item.rows, count(item.rows, lang))}${item.treatment ? `, ${text.details[item.treatment] || item.treatment}` : ""}`);

// What the analysis does with a branch's rows, as the backend computed it.
function branchTreatment(item, d) {
  const notes = [];
  if (item.attributedTo?.length) notes.push(d.branchAssigned(item.attributedTo.join("، ")));
  else if (item.excludedRows) notes.push(d.branchExcluded);
  else if (item.branchName && item.evidence !== "foreign_id") notes.push(d.matchedAs(item.branchName));
  if (item.candidates?.length) notes.push(d.candidates(item.candidates.join("، ")));
  if (item.alsoUsedByAnotherStore) notes.push(d.branchSharedName);
  return notes.map((note) => ` · ${note}`).join("");
}

function Pair({ label, children }) {
  return (
    <div className="report-issue__pair">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// Branch lists longer than BRANCH_PREVIEW are collapsed behind a "show all" toggle.
function BranchList({ items, text, lang }) {
  const d = text.details;
  const list = (
    <ul className="report-issue__list">
      {items.map((item, index) => (
        <li key={`${item.providerBranchId || item.providerBranchName}-${index}`}>
          <bdi>{item.providerBranchName || "—"}</bdi>
          {item.providerBranchId ? <> — {d.branchId}: <bdi className="report-issue__code">{item.providerBranchId}</bdi></> : null}
          <span className="report-analysis-muted"> · {d.rows(item.rows, count(item.rows, lang))}{branchTreatment(item, d)}</span>
          {item.rowNumbers?.length > 0 && <div>{d.unmatchedRows(item.rowNumbers.join("، "), item.rows - item.rowNumbers.length, item.providerBranchName || item.providerBranchId || "—", item.reason, item.rows)}</div>}
        </li>
      ))}
    </ul>
  );
  if (items.length <= BRANCH_PREVIEW) return list;
  return (
    <details className="report-issue__more">
      <summary>{d.showBranches(items.length, count(items.length, lang))}</summary>
      {list}
    </details>
  );
}

// item: an issue from reviewModel.buildReview (code, app, section, fileName + the backend's fields).
// kind: "error" | "warning". context: { storeName, period } of the analysis.
export default function IssueDetails({ item, kind, text, lang, context = {}, acknowledged = false }) {
  const d = text.details;
  const label = text.issues[item.code];
  const title = typeof label === "function" ? label(item.count) : label || item.message || item.code;
  const action = text.review.actions[item.code] || (kind === "error" ? text.review.actions.fallback : null);
  const actionText = typeof action === "function" ? action(appLabel(item.app, lang)) : action;
  // The period warnings show their own row breakdown instead of one "affected rows" figure.
  const affectedRows = [DAILY_PERIOD, MONTHLY_PERIOD].includes(item.code) ? null : item.affectedRows ?? (BRANCH.has(item.code) ? null : item.rows);
  const columns = item.actualColumns || [];
  const period = periodLabel(item.selectedPeriod || context.period, lang);
  return (
    <li className={`report-issue report-issue--${kind}`} data-code={item.code}>
      <div className="report-issue__head">
        <strong>{title}</strong>
        {acknowledged && <Tags variant="warning" size="sm">{text.review.acknowledgedTag}</Tags>}
      </div>
      <dl className="report-issue__pairs">
        {item.fileName && <Pair label={d.affectedFile}><bdi className="report-analysis-filename">{item.fileName}</bdi>{item.fileVersion ? ` · ${text.version(item.fileVersion)}` : ""}</Pair>}
        {item.section && item.app && <Pair label={d.section}>{sectionLabel(item.app, item.section, lang)}</Pair>}
        {item.sheet && <Pair label={d.worksheet}><bdi>{item.sheet}</bdi></Pair>}

        {HEADER.has(item.code) && Array.isArray(item.columns) && item.columns.length > 0 && (
          <Pair label={d.missingColumns}>{item.columns.map((column) => <code key={column} className="report-issue__code">{column}</code>)}</Pair>
        )}
        {HEADER.has(item.code) && columns.length > 0 && (
          <Pair label={d.actualColumns}>
            {columns.slice(0, COLUMN_PREVIEW).map((column) => <code key={column} className="report-issue__code">{column}</code>)}
            {(item.actualColumnCount || columns.length) > COLUMN_PREVIEW && <span className="report-analysis-muted"> {d.moreColumns((item.actualColumnCount || columns.length) - COLUMN_PREVIEW)}</span>}
          </Pair>
        )}
        {HEADER.has(item.code) && Array.isArray(item.expectedColumns) && (
          <Pair label={d.expectedColumns}>{item.expectedColumns.map((column) => <code key={column} className="report-issue__code">{column}</code>)}</Pair>
        )}

        {[CROSS_STORE, NOT_MATCHED].includes(item.code) && item.app && <Pair label={d.application}>{appLabel(item.app, lang)}</Pair>}
        {BRANCH.has(item.code) && <Pair label={d.selectedStore}>{item.selectedStore?.name || context.storeName || d.unknownStore}</Pair>}
        {BRANCH.has(item.code) && Array.isArray(item.matchedBranches) && (item.code === CROSS_STORE || item.matchedBranches.length > 0) && (
          <Pair label={d.matchedBranches}>{item.matchedBranches.length ? <BranchList items={item.matchedBranches} text={text} lang={lang} /> : d.noMatchedBranches}</Pair>
        )}
        {BRANCH.has(item.code) && Array.isArray(item.items) && item.items.length > 0 && (
          <Pair label={d.mismatchedBranches}><BranchList items={item.items} text={text} lang={lang} /></Pair>
        )}

        {PERIOD.has(item.code) && <Pair label={d.selectedPeriod}>{period}</Pair>}
        {PERIOD.has(item.code) && (item.sourcePeriods?.length > 0 || item.sourcePeriod) && (
          <Pair label={d.detectedPeriods}>
            {item.sourcePeriods?.length > 0
              ? <ul className="report-issue__list">{periodsText(item.sourcePeriods, lang, text).map((entry) => <li key={entry}>{entry}</li>)}</ul>
              : periodLabel(item.sourcePeriod, lang)}
          </Pair>
        )}
        {item.code === "SOURCE_PERIOD_INCONSISTENT" && item.statementPeriods?.length > 0 && (
          <Pair label={d.statementPeriods}>{periodsText(item.statementPeriods, lang, text).join("، ")}</Pair>
        )}
        {[DAILY_PERIOD, MONTHLY_PERIOD].includes(item.code) && typeof item.checkedRows === "number" && <Pair label={d.totalSourceRows}>{count(item.checkedRows, lang)}</Pair>}
        {item.code === DAILY_PERIOD && typeof item.includedRows === "number" && <Pair label={d.includedRows}>{count(item.includedRows, lang)}</Pair>}
        {item.code === DAILY_PERIOD && typeof item.excludedRows === "number" && <Pair label={d.excludedRows}>{count(item.excludedRows, lang)}</Pair>}
        {item.code === MONTHLY_PERIOD && typeof item.assignedRows === "number" && <Pair label={d.assignedRows(period)}>{count(item.assignedRows, lang)}</Pair>}
        {item.relatedFile && <Pair label={d.relatedFile}><bdi className="report-analysis-filename">{item.relatedFile}</bdi></Pair>}
        {item.code === "APP_NOT_CONFIGURED" && typeof item.excludedRows === "number" && <Pair label={d.excludedRows}>{count(item.excludedRows, lang)}</Pair>}

        {typeof affectedRows === "number" && <Pair label={item.code === CROSS_STORE ? d.totalAffectedRows : d.affectedRows}>{count(affectedRows, lang)}</Pair>}
        {item.reference && <Pair label={text.review.details}>{item.reference}{item.protections?.length ? ` — ${item.protections.map((entry) => text.protections[entry] || entry).join("، ")}` : ""}</Pair>}
        {kind === "error" && Array.isArray(item.samples) && item.samples.length > 0 && (
          <Pair label={d.samples}>{item.samples.slice(0, 3).map((sample) => `${sample.row ? `${text.review.row} ${sample.row}: ` : ""}${sample.value}`).join(" · ")}</Pair>
        )}
      </dl>

      {HEADER.has(item.code) && item.suggestions?.length > 0 && (
        <ul className="report-issue__suggestions">
          {item.suggestions.map((suggestion) => <li key={suggestion.expected}>{d.suggestion(suggestion.expected, suggestion.actual)}</li>)}
        </ul>
      )}
      {item.code === DAILY_PERIOD && typeof item.includedRows === "number" && <p className="report-issue__note">{d.dailyNote(count(item.includedRows, lang), count(item.excludedRows ?? 0, lang), period)}</p>}
      {item.code === MONTHLY_PERIOD && typeof item.assignedRows === "number" && <p className="report-issue__note">{d.monthlyNote(count(item.assignedRows, lang), period)}</p>}
      {item.code === "DUPLICATE_ROWS_ACROSS_PARTS" && <p className="report-issue__note">{d.duplicateNote}</p>}
      {item.code === CROSS_STORE && <p className="report-issue__note">{d.crossStoreNote(count(item.affectedRows || 0, lang), item.selectedStore?.name || context.storeName || d.unknownStore)}</p>}
      {actionText && <p className="report-issue__action"><strong>{d.action}:</strong> {actionText}</p>}
    </li>
  );
}

// A list of issues of one kind.
export function IssueList({ items, kind, text, lang, context, acknowledged = false }) {
  if (!items?.length) return null;
  return (
    <ul className={`report-issues report-issues--${kind}`}>
      {items.map((item, index) => <IssueDetails key={`${item.code}-${item.fileName || ""}-${index}`} item={item} kind={kind} text={text} lang={lang} context={context} acknowledged={acknowledged} />)}
    </ul>
  );
}
